/**
 * FindMine PDP Integration - "Complete the Look" Widget
 *
 * Lazy-loads FindMine's product recommendation widget on product detail pages.
 * Uses IntersectionObserver to only load the script when the container becomes visible,
 * improving initial page load performance.
 *
 * Implementation guide: https://findmine.notion.site/PDP-Widget-Implementation-Guide-18f008cf0c714960888a7c3c78c6f70c
 *
 * @requires window.theme.apps.findmine.pdp_application_id - FindMine PDP application ID
 * @requires #findmine-app-container - DOM element where FindMine widget will render
 * @listens product-overview:swatch-change - Fires when user selects a different color/variant
 */
import { EVENTS, subscribe } from '@utilities/events';

if (!theme.apps?.findmine?.pdp_application_id) {
  console.warn("FindMine integration: FindMinePDPApplicationID is not set.");
}

/**
 * Builds a FindMine.match() compatible object from product and variant data.
 *
 * @param {Object} product - Product data from product-overview component
 * @param {string|number} product.id - Product ID
 * @param {Object} variant - Product variant data
 * @param {string} variant.color - Color/variant identifier
 * @param {boolean} variant.available - Whether the variant is in stock
 * @returns {Object|null} FindMine match configuration object, or null if invalid
 */
const buildFindMineItem = (product, variant) => {
  if (!variant || Object.keys(variant).length <= 1) return null;

  return {
    application: theme.apps?.findmine?.pdp_application_id,
    product_id: product?.id,
    product_color_id: variant.color,
    product_in_stock: variant.available,
    return_pdp_item: true,
    event_handlers: {
      onFinish: () => {
        // console.log('PDP FindMine Loaded')
      },
    },
  };
};

/**
 * Handles variant changes and triggers FindMine match.
 *
 * When a user selects a different color or variant, this function builds
 * the match configuration and sends it to FindMine's API to get updated recommendations.
 *
 * @param {Object} product - Product data
 * @param {Object} variant - Product variant data
 */
const handleVariantChange = (product, variant) => {
  if (!variant || Object.keys(variant).length <= 1) return;
  const findMineItem = buildFindMineItem(product, variant);
  if (findMineItem && Object.keys(findMineItem).length > 1) {
    try {
      window.FindMine?.match(findMineItem);
    } catch (error) {
      console.error("Error matching FindMine: ", error);
    }
  }
};

/**
 * Initializes FindMine integration
 *
 * Sets up event listener for variant changes and performs initial match
 * with the currently selected variant. Called after FindMine script loads.
 */
const initializeFindMine = () => {
  subscribe(EVENTS.PRODUCT_OVERVIEW_SWATCH_CHANGE, (data) => {
    const product = data?.productOverview?.getProduct();
    handleVariantChange(product, data?.variant);
  });
  const productOverview = document.querySelector("product-overview");
  if (productOverview) {
    handleVariantChange(productOverview.getProduct(), productOverview.getVariant());
  }
};

/**
 * Loads the FindMine external script
 *
 * Creates and injects the FindMine script tag into the page.
 * Once loaded, initializes the FindMine integration.
 */
const loadFindMineScript = () => {
  const script = document.createElement("script");
  script.src = `//js.findmine.com/${window.theme.apps.findmine.pdp_application_id}.module.js`;
  script.onload = initializeFindMine;
  document.head.appendChild(script);
};

/**
 * Sets up lazy loading for FindMine using IntersectionObserver
 *
 * Monitors the #findmine-app-container element and only loads the FindMine
 * script when the container is about to enter the viewport (200px before).
 * This improves initial page load performance by deferring non-critical resources.
 *
 * Once the script loads and initializes, the observer is disconnected as the
 * variant change listener handles all future interactions.
 */
const observeFindMineContainer = () => {
  const findmineContainer = document.querySelector('#findmine-app-container');

  // If container doesn't exist, don't load FindMine
  if (!findmineContainer) {
    // console.log('FindMine: Container not found, skipping initialization');
    return;
  }

  // Create intersection observer with a small threshold
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // console.log('FindMine: Container is visible, loading script');
        loadFindMineScript();
        // Stop observing after first intersection
        observer.disconnect();
      }
    });
  }, {
    // Start loading when container is about to enter viewport (200px before)
    rootMargin: '200px',
    threshold: 0
  });

  // Start observing the FindMine container
  observer.observe(findmineContainer);
};

// Start observing for the FindMine container
observeFindMineContainer();
